import { createSlice } from '@reduxjs/toolkit';

export const addressSlice = createSlice({
    name: 'address',
    initialState: {
        loading: false,
        success: false,
        error: null,
        address: null,
        addresses: [],
        addressDetailLoading: false,
        addressDetailSuccess: false,
        addressDetailError: null,
        addressUpdateLoading: false,
        addressUpdateSuccess: false,
        addressUpdateError: null,
        addressDeleteLoading: false,
        addressDeleteSuccess: false,
        addressDeleteError: null,
        addressCreateLoading: false,
        addressCreateSuccess: false,
        addressCreateError: null,
    },
    reducers: {

        // Addresses
        addressListRequest: (state) => { 
            state.loading = true;
            state.error = null;
            // Don't clear addresses here to prevent flickering
        },
        addressListSuccess: (state, action) => { 
            state.loading = false; 
            state.success = true; 
            state.error = null;
            
            // Debug logging for addressListSuccess
            console.log('Address list reducer - received payload:', action.payload);
            
            // Ensure addresses is always an array
            if (Array.isArray(action.payload)) {
                state.addresses = action.payload;
            } else if (action.payload) {
                state.addresses = [action.payload];
            } else {
                state.addresses = [];
            }
            
            // Debug logging after setting state
            console.log('Address list reducer - updated addresses:', state.addresses);
        },
        addressListFail: (state, action) => { 
            state.loading = false; 
            state.error = action.payload;
            // Don't clear addresses here to show stale data
        },

        // Address Detail
        addressDetailRequest: (state) => { state.addressDetailLoading = true },
        addressDetailSuccess: (state, action) => { state.addressDetailLoading = false; state.addressDetailSuccess = true; state.address = action.payload },
        addressDetailFail: (state, action) => { state.addressDetailLoading = false; state.addressDetailError = action.payload },

        // Update Address
        addressUpdateRequest: (state) => { state.addressUpdateLoading = true },
        addressUpdateSuccess: (state, action) => { state.addressUpdateLoading = false; state.addressUpdateSuccess = true; state.address = action.payload },
        addressUpdateFail: (state, action) => { state.addressUpdateLoading = false; state.addressUpdateError = action.payload },

        // Delete Address
        addressDeleteRequest: (state) => { state.addressDeleteLoading = true },
        addressDeleteSuccess: (state, action) => { 
            state.addressDeleteLoading = false; 
            state.addressDeleteSuccess = true; 
            

            
            // Make sure we're using the correct ID for filtering
            const deleteId = action.payload?._id || action.payload?.id;
                     
            // Only filter if we have a valid array of addresses
            if (state.addresses && Array.isArray(state.addresses)) {
                state.addresses = state.addresses.filter((address) => {
                    return address._id !== deleteId;
                });
            } else {
                state.addresses = [];
            }
            

        },
        addressDeleteFail: (state, action) => { state.addressDeleteLoading = false; state.addressDeleteError = action.payload },

        // Create Address
        addressCreateRequest: (state) => { state.addressCreateLoading = true },
        addressCreateSuccess: (state, action) => { 
            state.addressCreateLoading = false; 
            state.addressCreateSuccess = true;
            
            // Defensively handle the address data
            const newAddress = action.payload;
            if (newAddress) {
                // Check if addresses exists and is an array, otherwise initialize it
                if (!state.addresses || !Array.isArray(state.addresses)) {
                    state.addresses = [];
                }
                // Add the new address to the array
                state.addresses.push(newAddress);
            }
        },
        addressCreateFail: (state, action) => { state.addressCreateLoading = false; state.addressCreateError = action.payload },

        // Reset Actions
        addressListReset: (state) => { state.loading = false; state.success = false; state.error = null },
        addressDetailReset: (state) => { state.addressDetailLoading = false; state.addressDetailSuccess = false; state.addressDetailError = null },
        addressUpdateReset: (state) => { state.addressUpdateLoading = false; state.addressUpdateSuccess = false; state.addressUpdateError = null },
        addressDeleteReset: (state) => { state.addressDeleteLoading = false; state.addressDeleteSuccess = false; state.addressDeleteError = null },
        addressCreateReset: (state) => { state.addressCreateLoading = false; state.addressCreateSuccess = false; state.addressCreateError = null },

        // Reset Entire Slice
        addressSliceReset: (state) => {
            return {
                loading: false,
                success: false,
                error: null,
                address: null,
                addresses: [],
                addressDetailLoading: false,
                addressDetailSuccess: false,
                addressDetailError: null,
                addressUpdateLoading: false,
                addressUpdateSuccess: false,
                addressUpdateError: null,
                addressDeleteLoading: false,
                addressDeleteSuccess: false,
                addressDeleteError: null,
                addressCreateLoading: false,
                addressCreateSuccess: false,
                addressCreateError: null,
            };
        },
    }
});

export const {
    addressListRequest,
    addressListSuccess,
    addressListFail,
    addressListReset,

    addressDetailRequest,
    addressDetailSuccess,
    addressDetailFail,
    addressDetailReset,

    addressUpdateRequest,
    addressUpdateSuccess,
    addressUpdateFail,
    addressUpdateReset,

    addressDeleteRequest,
    addressDeleteSuccess,
    addressDeleteFail,
    addressDeleteReset,

    addressCreateRequest,
    addressCreateSuccess,
    addressCreateFail,
    addressCreateReset,

    addressSliceReset,
} = addressSlice.actions;

export default addressSlice.reducer;
